import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyBlockchaingovernanceMode({ brand, today, Copy }: ModeRendererProps) {
  // Blockchain governance gradient: trust verification
  const blockchainGradient = (
    <defs>
      <linearGradient id="blockchainGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1E3A8A" />
        <stop offset="50%" stopColor="#059669" />
        <stop offset="100%" stopColor="#F59E0B" />
      </linearGradient>
    </defs>
  );

  // Merkle trees: cryptographic verification
  const merkleTrees: JSX.Element[] = [];
  const levels = 5;
  for (let level = 0; level < levels; level++) {
    const nodeCount = Math.pow(2, level);
    const y = 200 + level * 120;
    for (let i = 0; i < nodeCount; i++) {
      const x = 397 - (nodeCount - 1) * 60 / 2 + i * 60;
      merkleTrees.push(
        <g key={`merkle-${level}-${i}`}>
          <rect
            x={x - 20}
            y={y - 15}
            width={40}
            height={30}
            fill="url(#blockchainGrad)"
            opacity={0.4}
            rx={4}
            stroke="url(#blockchainGrad)"
            strokeWidth={1.5}
          />
          {level < levels - 1 && (
            <>
              <line
                x1={x}
                y1={y + 15}
                x2={x - 30}
                y2={y + 105}
                stroke="url(#blockchainGrad)"
                strokeWidth={1}
                opacity={0.3}
              />
              <line
                x1={x}
                y1={y + 15}
                x2={x + 30}
                y2={y + 105}
                stroke="url(#blockchainGrad)"
                strokeWidth={1}
                opacity={0.3}
              />
            </>
          )}
        </g>
      );
    }
  }

  // Block lattices: blockchain structure
  const blockLattices: JSX.Element[] = [];
  for (let i = 0; i < 14; i++) {
    const x = 120 + i * 42;
    const y = 820 + (i % 3) * 60;
    blockLattices.push(
      <g key={`block-${i}`}>
        <rect
          x={x - 18}
          y={y - 25}
          width={36}
          height={50}
          fill="none"
          stroke="url(#blockchainGrad)"
          strokeWidth={2}
          opacity={0.5}
          rx={4}
        />
        {i > 0 && (
          <line
            x1={x - 18}
            y1={y}
            x2={x - 24}
            y2={y}
            stroke="#F59E0B"
            strokeWidth={2}
            opacity={0.6}
          />
        )}
      </g>
    );
  }

  // Consensus rings: distributed agreement
  const consensusRings: JSX.Element[] = [];
  for (let i = 0; i < 16; i++) {
    const angle = (i * Math.PI * 2) / 16;
    const radius = 200;
    const x = 397 + radius * Math.cos(angle);
    const y = 561.5 + radius * Math.sin(angle);
    consensusRings.push(
      <g key={`consensus-${i}`}>
        <circle cx={x} cy={y} r={18} fill="url(#blockchainGrad)" opacity={0.3} />
        <circle cx={x} cy={y} r={12} fill="none" stroke="url(#blockchainGrad)" strokeWidth={2} />
        <circle cx={x} cy={y} r={6} fill="#059669" opacity={0.6} />
      </g>
    );
  }

  // Verification connections
  consensusRings.forEach((_, i) => {
    const angle1 = (i * Math.PI * 2) / 16;
    const angle2 = ((i + 1) * Math.PI * 2) / 16;
    const radius = 200;
    const x1 = 397 + radius * Math.cos(angle1);
    const y1 = 561.5 + radius * Math.sin(angle1);
    const x2 = 397 + radius * Math.cos(angle2);
    const y2 = 561.5 + radius * Math.sin(angle2);
    consensusRings.push(
      <line
        key={`verify-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#059669"
        strokeWidth={1.5}
        opacity={0.4}
      />
    );
  });

  // Central trust core
  const trustCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={90} fill="none" stroke="#F59E0B" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={65} fill="none" stroke="#059669" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={40} fill="none" stroke="#1E3A8A" strokeWidth={2} />
      {/* Lock symbol */}
      <rect x={377} y={551.5} width={40} height={30} rx={4} fill="none" stroke="#FFFFFF" strokeWidth={2.5} />
      <path
        d="M 385 551.5 L 385 543.5 Q 385 533.5 397 533.5 Q 409 533.5 409 543.5 L 409 551.5"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={2.5}
      />
    </g>
  );

  return (
    <g>
      {blockchainGradient}
      {merkleTrees}
      {blockLattices}
      {consensusRings}
      {trustCore}
      {/* Render Copy object safely (Copy from constants, not a component) */}
      {Copy && typeof Copy === "object" && (
        <g fontFamily="monospace" fontSize={9} fill="#FFFFFF" opacity={0.9}>
          <text x={397} y={1040} textAnchor="middle" fontWeight={700}>
            {Copy.title}
          </text>
          <text x={397} y={1072} textAnchor="middle">
            {/* date/version removed */}
          </text>
          <text x={397} y={1072} textAnchor="middle">
            {Copy.date} {Copy.version ? `— ${Copy.version}` : ""}
          </text>
        </g>
      )}
    </g>
  );
}
